'use client';

import { personalData } from '@/utils/data/personal-data';
import Image from 'next/image';
import Link from 'next/link';
import { RiContactsFill } from 'react-icons/ri';
import { Typewriter } from 'react-simple-typewriter';
import { EyeIcon } from '../icons/EyeIcon';
import { FacebookIcon } from '../icons/FacebookIcon';
import { GithubIcon } from '../icons/GithubIcon';
import { LinkedInIcon } from '../icons/LinkedinIcon';
import { TwitterIcon } from '../icons/TwitterIcon';

const texts = [
  'A Full Stack Developer',
  'A Passionate Coder',
  'An Enthusiastic Learner',
  'A Problem Solver',
];

export default function HeroSection() {
  return (
    <section className='relative flex flex-col items-center justify-between py-4 lg:py-12'>
      <Image
        src='/hero.webp'
        alt='Abstract hero background'
        width={1572}
        height={795}
        priority
        fetchPriority='high'
        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw'
        className='absolute -top-24.5 -z-10 w-full max-w-screen object-cover'
      />
      <div className='grid grid-cols-1 items-start gap-y-8 lg:grid-cols-2 lg:gap-12'>
        <div className='order-2 flex flex-col items-start justify-center p-2 pb-20 text-left md:pb-10 lg:order-1 lg:pt-10'>
          <h1 className='text-3xl leading-10 font-bold text-white md:font-extrabold lg:text-[2.6rem] lg:leading-14'>
            Hello, <br />
            This is{' '}
            <span className='text-pink-500 transition-colors duration-300 hover:text-[#16f2b3]'>
              {personalData.name}
            </span>{' '}
            <br />
            <span className='text-[#16f2b3]'>
              <Typewriter words={texts} loop={true} />
            </span>
          </h1>

          <div className='my-8 flex w-full justify-center gap-5 lg:justify-start'>
            <Link
              href={personalData.github}
              target='_blank'
              aria-label='GitHub'
              className='text-pink-500 transition-all duration-300 hover:scale-125 hover:text-[#16f2b3]'
            >
              <GithubIcon size={30} />
            </Link>
            <Link
              href={personalData.linkedIn}
              target='_blank'
              aria-label='LinkedIn'
              className='text-pink-500 transition-all duration-300 hover:scale-125 hover:text-[#16f2b3]'
            >
              <LinkedInIcon size={30} />
            </Link>
            <Link
              href={personalData.facebook}
              target='_blank'
              aria-label='Facebook'
              className='text-pink-500 transition-all duration-300 hover:scale-125 hover:text-[#16f2b3]'
            >
              <FacebookIcon size={30} />
            </Link>
            <Link
              href={personalData.twitter}
              target='_blank'
              aria-label='Twitter'
              className='text-pink-500 transition-all duration-300 hover:scale-125 hover:text-[#16f2b3]'
            >
              <TwitterIcon size={30} />
            </Link>
          </div>

          <div className='flex w-full flex-wrap items-center justify-center gap-3 lg:justify-start'>
            <Link href='#contact' scroll>
              <button className='flex items-center gap-1 rounded-full bg-linear-to-r from-violet-500 to-emerald-400 p-3 text-sm font-medium tracking-wider whitespace-nowrap text-white uppercase transition-all duration-200 ease-out hover:gap-3 hover:from-pink-500 hover:to-cyan-500'>
                <span>Contact me</span>
                <RiContactsFill size={16} />
              </button>
            </Link>

            <Link
              className='flex items-center gap-1 rounded-full bg-linear-to-r from-emerald-400 to-violet-500 p-3 text-sm font-medium tracking-wider whitespace-nowrap text-white uppercase transition-all duration-200 ease-out hover:gap-3 hover:from-cyan-500 hover:to-pink-500'
              role='button'
              target='_blank'
              href={personalData.resume}
            >
              <span>View Resume</span>
              <EyeIcon size={16} />
            </Link>
          </div>
        </div>

        <div className='relative order-1 rounded-lg border border-[#1b2c68a0] bg-linear-to-r from-[#0d1224] to-[#0a0d37] lg:order-2'>
          <div className='flex flex-row'>
            <div className='h-px w-full bg-linear-to-r from-transparent via-pink-500 to-violet-600' />
            <div className='h-px w-full bg-linear-to-r from-violet-600 to-transparent' />
          </div>

          <div className='px-4 py-5 lg:px-8'>
            <div className='flex flex-row space-x-2'>
              <div className='h-3 w-3 rounded-full bg-red-400' />
              <div className='h-3 w-3 rounded-full bg-orange-400' />
              <div className='h-3 w-3 rounded-full bg-green-200' />
            </div>
          </div>

          <div className='overflow-hidden border-t-2 border-indigo-900 px-4 py-4 lg:px-8 lg:py-8'>
            <code className='font-mono text-xs md:text-sm lg:text-base'>
              <div className='blink'>
                <span className='mr-2 text-pink-500'>const</span>
                <span className='mr-2 text-white'>coder</span>
                <span className='mr-2 text-pink-500'>=</span>
                <span className='text-gray-400'>{'{'}</span>
              </div>
              <div>
                <span className='mr-2 ml-4 text-white lg:ml-8'>name:</span>
                <span className='text-gray-400'>{`'`}</span>
                <span className='text-amber-300'>Praveen Singh</span>
                <span className='text-gray-400'>{`',`}</span>
              </div>
              <div className='mr-2 ml-4 lg:ml-8'>
                <span className='text-white'>skills:</span>
                <span className='text-gray-400'>{`['`}</span>
                <span className='text-amber-300'>ReactJS</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>NextJS</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>NodeJS</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>ExpressJS</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>TailwindCSS</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>Typescript</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>MongoDB</span>
                <span className='text-gray-400'>{"', '"}</span>
                <span className='text-amber-300'>Supabase</span>
                <span className='text-gray-400'>{"'],"}</span>
              </div>
              <div>
                <span className='mr-2 ml-4 text-white lg:ml-8'>hardWorker:</span>
                <span className='text-orange-400'>true</span>
                <span className='text-gray-400'>,</span>
              </div>
              <div>
                <span className='mr-2 ml-4 text-white lg:ml-8'>quickLearner:</span>
                <span className='text-orange-400'>true</span>
                <span className='text-gray-400'>,</span>
              </div>
              <div>
                <span className='mr-2 ml-4 text-white lg:ml-8'>problemSolver:</span>
                <span className='text-orange-400'>true</span>
                <span className='text-gray-400'>,</span>
              </div>
              <div>
                <span className='mr-2 ml-4 text-green-400 lg:ml-8'>hireable:</span>
                <span className='text-orange-400'>function</span>
                <span className='text-gray-400'>{'() {'}</span>
              </div>
              <div>
                <span className='mr-2 ml-8 text-orange-400 lg:ml-16'>return</span>
                <span className='text-gray-400'>{`(`}</span>
              </div>
              <div>
                <span className='ml-12 text-cyan-400 lg:ml-24'>this.</span>
                <span className='mr-2 text-white'>hardWorker</span>
                <span className='text-amber-300'>&amp;&amp;</span>
              </div>
              <div>
                <span className='ml-12 text-cyan-400 lg:ml-24'>this.</span>
                <span className='mr-2 text-white'>problemSolver</span>
                <span className='text-amber-300'>&amp;&amp;</span>
              </div>
              <div>
                <span className='ml-12 text-cyan-400 lg:ml-24'>this.</span>
                <span className='mr-2 text-white'>skills.length</span>
                <span className='mr-2 text-amber-300'>&gt;=</span>
                <span className='text-orange-400'>5</span>
              </div>
              <div>
                <span className='mr-2 ml-8 text-gray-400 lg:ml-16'>{`);`}</span>
              </div>
              <div>
                <span className='ml-4 text-gray-400 lg:ml-8'>{`};`}</span>
              </div>
              <div>
                <span className='text-gray-400'>{`};`}</span>
              </div>
            </code>
          </div>
        </div>
      </div>
    </section>
  );
}
